import { useCallback, useEffect, useState, FormEvent } from 'react';
import { TextField, Button, Alert } from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { InvitedUser, listUsers, inviteUser, removeUser } from '../lib/api';
import './ManageUsersPage.css';

function formatStatus(status: string): string {
  return status === 'FORCE_CHANGE_PASSWORD' ? 'Invited (pending)' : 'Active';
}

export function ManageUsersPage() {
  const { isAuthenticated, idToken, email: currentEmail } = useAuth();
  const [users, setUsers] = useState<InvitedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(() => {
    if (!idToken) return;
    listUsers(idToken)
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [idToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!idToken) return;

    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await inviteUser(email, firstName, lastName, idToken);
      setSuccess(`Invite sent to ${email}.`);
      setEmail('');
      setFirstName('');
      setLastName('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (user: InvitedUser) => {
    if (!idToken) return;
    if (!window.confirm(`Remove access for ${user.email}?`)) return;
    try {
      await removeUser(user.email, idToken);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="manage-users-page">
        <div className="section-inner">
          <h1>Manage Users</h1>
          <p>Sign in to invite other editors.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="manage-users-page">
      <div className="section-inner">
        <h1>Manage Users</h1>
        <p className="manage-users-subcopy">
          Invite other people to sign in and manage the content of this website.
        </p>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <form className="manage-users-invite-form" onSubmit={handleInvite}>
          <TextField
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            size="small"
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            size="small"
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <Button type="submit" variant="contained" disableElevation disabled={submitting}>
            {submitting ? 'Sending…' : 'Send Invite'}
          </Button>
        </form>

        {!loading && users.length === 0 && <p>No users yet.</p>}

        {users.length > 0 && (
          <table className="manage-users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.email}>
                  <td>{[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}</td>
                  <td>{user.email}</td>
                  <td>{formatStatus(user.status)}</td>
                  <td className="manage-users-table-actions">
                    {user.email !== currentEmail && (
                      <button type="button" onClick={() => handleRemove(user)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
